/* eslint-disable eqeqeq */
import React, { useEffect, useState } from "react";
import MeetingSdkView from "../../../components/mediaplayer/meeting-sdk";
import SlidesDisplay from "../../../components/slider/producer-slider/producer-slider-view";
import "./producer-dashboard.css";
import PresenterParticipantsView from "../../../components/qna/participants";
import PresenterChatView from "../../../components/qna/chat-view";
import PptViewer from "../../../components/slider/producer-slider/producer-ppt-view";
import { EventFetchSlides } from "../../../services/event/attendee-event-slides";
import { useParams } from "react-router-dom";
import Loader from "../../../utils/loader/loader";
import LivePopup from "../../../components/pop-up/live-pop-up";
import { fetchPollAllResults } from "../../../services/event/pollAllResults";
import { generateClient } from 'aws-amplify/api';
import * as config from '../../../aws-exports';
import { toggleSlideControl } from '../../../graphql/mutations';
import { onMeetingStatusChange, onSlideControlToggle } from "../../../graphql/subscriptions";
import { formatInTimeZone } from 'date-fns-tz';
import moment from "moment-timezone";

const client = generateClient(config);

interface SdkData {
  meetingNumber: string;
  password: string;
  userName: string;
  userEmail: string;
  signature: string;
  tk: string;
  role: number;
}

interface Question {
  pollId: string;
  questionTitle: string;
  options: Record<string, string>;
  sequence: number;
}

interface PollResultData {
  questionTitle: string;
  totalVotes: number;
  results: PollResult[];
}

interface PollResult {
  option: string;
  percentage: number;
  voteCount: number;
}

const ProducerDashboardView: React.FC<{ sdkData: SdkData; eventData: any }> = ({
  sdkData,
  eventData,
}) => {
  const { eventId } = useParams<{ eventId: string }>();

  if (!eventId) {
    throw new Error("Event ID is required but was not provided.");
  }

  const [selectedSlideSequence, setSelectedSlideSequence] = useState<number>(0);
  const [slides, setSlides] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [pollQuestions, setPollQuestions] = useState<Question[]>([]);
  const [allPollResults, setAllPollResults] = useState<PollResultData[]>([]);
  const [slideControlEnabled, setSlideControlEnabled] = useState<boolean>(false);
  const [sliderTop, setSliderTop] = useState(0);
  const [meetingStatus, setMeetingStatus] = useState<boolean>(false);
  const [timeUntilStart, setTimeUntilStart] = useState('');
  const [timeUntilEnd, setTimeUntilEnd] = useState('');
  const [isWebinarStarted, setIsWebinarStarted] = useState(false);
  const [isSlides, setIsSlides] = useState<boolean>();
  const webinarStartTime = new Date(eventData.date) // Replace with your actual start time
  const webinarDuration = eventData.duration * 60 * 1000; // 45 minutes (or any other duration)
  const eventDate = moment(eventData.date).format('dddd, MMMM Do YYYY');
  const eventTime = moment(eventData.date).format('h:mm A z');
  const eventShotTimeZone = moment(eventData.date).tz(eventData.timeZone).format('z');


  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nowInET = new Date(
        formatInTimeZone(now, eventData.timeZone, 'yyyy-MM-dd HH:mm:ss')
      );

      // const webinarStartInET = new Date(
      //   formatInTimeZone(webinarStartTime, 'America/New_York', 'yyyy-MM-dd HH:mm:ss')
      // );

      const timeToStart = webinarStartTime.getTime() - nowInET.getTime();
      const timeToEnd = webinarStartTime.getTime() + webinarDuration - nowInET.getTime();

      if (timeToStart > 0) {
        setTimeUntilStart(formatTimeWithShortcuts(timeToStart));
        setTimeUntilEnd('');
        setIsWebinarStarted(false);
      } else if (timeToEnd > 0) {
        setTimeUntilStart('Webinar Started');
        setTimeUntilEnd(formatTimeWithShortcuts(timeToEnd));
        setIsWebinarStarted(true);
      } else {
        setTimeUntilStart('Webinar Ended');
        setTimeUntilEnd('');
      }
    };

    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinarStartTime, webinarDuration]);

  // Function to format time with abbreviated units
  const formatTimeWithShortcuts = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let result = '';
    if (hours > 0) {
      result += `${hours}h `;
    }
    if (minutes > 0) {
      result += `${minutes}m `;
    }
    result += `${seconds}s`;

    return result;
  };

  const handleGoLiveClick = () => {
    setIsPopupVisible(true);
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await EventFetchSlides(Number(eventId));
        setSlides(response?.collaterals?.slides);
        setPollQuestions(response.collaterals.pollQuestions);
        setMeetingStatus(response.isMeetingLive);
        if (response?.collaterals?.slides.length === 0) {
          setIsSlides(false);
        } else {
          setIsSlides(true);
        }
      } catch (error) {
        console.error("Error fetching slides:", error);
      }
    };

    fetchData();
  }, [eventId]);

  useEffect(() => {
    const fetchPollResult = async () => {
      try {
        const response = await fetchPollAllResults(Number(eventId));
        setAllPollResults(response); // Ensure this response matches the PollResultData[] type
      } catch (error) {
        console.error("Error fetching poll results:", error);
      }
    };

    fetchPollResult();
  }, [eventId]);

  const slideControlClick = async () => {
    try {
      const newStatus = !slideControlEnabled;
      await client.graphql({
        query: toggleSlideControl,
        variables: { eventid: eventId, enableSlideControl: newStatus }, // Pass the dynamic value here
      });
      setSlideControlEnabled(newStatus); // Update the state to reflect the new status
    } catch (error) {
      console.error("Error toggling slide control:", error);
    }
  };

  const setSliderTopFn = (heightRef: number) => {
    const heightToSlider = heightRef - 235;
    setSliderTop(heightToSlider);
  };

  useEffect(() => {
    // Subscribe to slide control toggle
    const subscription = client.graphql({ query: onSlideControlToggle });
    const observer = subscription.subscribe({
      next: ({ data }) => {
        const controlData = data.onSlideControlToggle;
        if (controlData.eventid === eventId) {
          setSlideControlEnabled(controlData.enableSlideControl);
        }
      },
      error: (error) => console.warn('Subscription error:', error),
    });

    return () => observer.unsubscribe();
  }, [eventId]);

  useEffect(() => {
    const subscription = client.graphql({ query: onMeetingStatusChange });
    const observer = subscription.subscribe({
      next: ({ data }) => {
        const newSlide = data.onMeetingStatusChange;
        if (newSlide && newSlide.eventid == eventId) {
          setMeetingStatus(newSlide.meetingstatusflag);
        }
      },
      error: (error) => console.warn('Subscription error:', error),
    });

    return () => observer.unsubscribe();
  }, [eventId]);

  return (
    <div id="host-dash" className="main-panel-wrapper">
      {isLoading && <Loader />}
      <div className="presenter-header">
        <div className="left-data">
          {/* <img src={logo} className="logo-wrapper" alt="Logo" /> */}
          <div className="event-title">{eventData?.title}</div>
        </div>
        <div className="right-data">
        <div className="event-timedata">
         {!isWebinarStarted && (
        <>
         <p>
          Webinar Start Time:{' '}
          {eventDate} | {eventTime} {eventShotTimeZone} 
          </p>
          <p>Webinar Starts In: {timeUntilStart}</p>
        </>
         )}
         {isWebinarStarted && (
         <p>Webinar Ends In: {timeUntilEnd}</p>
         )}
         </div>
          {sdkData.role === 1 && (
            <>
              <button onClick={slideControlClick} className="slide-control-btn">
                {slideControlEnabled ? "Disable Speaker Slide Control" : "Enable Speaker Slide Control"}
              </button>

              <button onClick={handleGoLiveClick} className="live-btn">
                {meetingStatus ? "End webinar" : "Go Live"}
              </button>
            </>
          )}
          {sdkData.role === 0 && (
            <div className="slide-control-status-msg">
              {slideControlEnabled
                ? "Slide Control is currently enabled."
                : "Slide Control is currently disabled."}
            </div>
          )}
          {isPopupVisible && (
            <LivePopup
              sdkData={sdkData}
              setIsPopupVisible={setIsPopupVisible}
              onClose={handleClosePopup}
              mStatus={meetingStatus}
            />
          )}
        </div>
      </div>
      <div className="inner-main-wrapper">
          <div className="left-side-wrapper">
            <MeetingSdkView
              meetingNumber={sdkData.meetingNumber}
              password={sdkData.password}
              userName={sdkData.userName}
              userEmail={sdkData.userEmail}
              leaveUrl="https://example.com"
              signature={sdkData.signature}
              tk={sdkData.tk}
              setLoading={setIsLoading}
              getVideoHeight={setSliderTopFn}
              isSlides ={isSlides}
            />
          <div className="current-slide-wrapper">
            <PptViewer slidesData={slides} pollData={pollQuestions} allPollResultData={allPollResults} />
          </div>
        {isSlides &&(
          <div className="slider-display" style={{ marginTop: sliderTop }}>
            <div className="inner-slider-wrapper">
              <SlidesDisplay
                onSlideSelect={setSelectedSlideSequence}
                selectedSlideSequence={selectedSlideSequence}
                slidesData={slides}
                pollData={pollQuestions}
                allPollResultData={allPollResults}
                role={sdkData.role}
              />
            </div>
          </div>
        )}
        </div>
          <div className="pro-chat-wrapper">
            <PresenterChatView />
          </div>
          <div className="participant-wrapper">
            <PresenterParticipantsView />
          </div>
      </div>
    </div>
  );
};

export default ProducerDashboardView;
